/**
 * Authentication Token Interceptor (Functional)
 *
 * This interceptor automatically adds authentication tokens to HTTP requests
 * and handles token refresh when tokens expire. It uses the GlobalAuthStateService
 * to get tokens for any authentication provider.
 */
import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { GlobalAuthStateService } from '../services/global-auth-state.service';
import { MicrosoftAuthService } from '../services/microsoft-auth.service';
import { LoggerService } from '../services/logger.service';

/**
 * Authentication token interceptor for automatic token injection
 * and token refresh on 401 responses
 */
export const authTokenInterceptor: HttpInterceptorFn = (req, next) => {
  const globalAuthState = inject(GlobalAuthStateService);
  const msalAuthService = inject(MicrosoftAuthService);
  const logger = inject(LoggerService);

  // Skip authentication for certain URLs
  if (shouldSkipAuth(req.url)) {
    return next(req);
  }

  // Get current authentication token from global state
  const authToken = globalAuthState.accessToken;

  // Add token to request if available
  const authReq = authToken ? addAuthHeader(req, authToken) : req;

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // Handle 401 Unauthorized - token expired or invalid
      if (error.status === 401 && authToken) {
        return handle401Error(req, next, globalAuthState, msalAuthService, logger);
      }

      return throwError(() => error);
    })
  );
};

/**
 * Check if request should skip authentication
 */
function shouldSkipAuth(url: string): boolean {
  const skipUrls = [
    '/authorizeuser',
    '/refreshtoken',
    '/validatetoken',
    '/login',
    '/register',
    '/forgot-password',
    '/reset-password',
    '/assets/',
    '.json',
  ];

  return skipUrls.some((skipUrl) => url.includes(skipUrl));
}

/**
 * Add authentication header to request
 */
function addAuthHeader(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });
}

/**
 * Handle 401 error by attempting token refresh
 */
function handle401Error(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  globalAuthState: GlobalAuthStateService,
  msalAuthService: MicrosoftAuthService,
  logger: LoggerService
) {
  const currentProvider = globalAuthState.currentProvider;

  // MSAL/Microsoft: attempt acquireTokenSilent, then retry
  if (currentProvider === 'microsoft') {
    return from(msalAuthService.acquireTokenSilently()).pipe(
      switchMap(() => {
        const newToken = globalAuthState.accessToken;
        const retryRequest = newToken ? addAuthHeader(request, newToken) : request;
        return next(retryRequest);
      }),
      catchError((refreshError: unknown) => {
        logger.error('MSAL token refresh failed:', refreshError);
        globalAuthState.clearAuthState();
        return throwError(() => new Error('Microsoft authentication expired'));
      })
    );
  }

  // Google/Apple: no silent refresh available — clear state and throw error
  if (currentProvider === 'google' || currentProvider === 'apple') {
    globalAuthState.clearAuthState();
    return throwError(() => new Error(`${currentProvider} authentication expired`));
  }

  // No provider or unknown provider — clear state and throw
  globalAuthState.clearAuthState();
  return throwError(() => new Error('Authentication expired'));
}
